/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState, type ReactNode } from 'react';
import { describeLeaseRefusal } from '../utils/lease-refusals';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button, Text, Textarea } from '@/shared/ui';
import {
  getActionsForState,
  useLeaseActionLabel,
  type LeaseAction,
} from '../constants/lease-states';
import type { LeaseTransitionBody } from '../hooks/useLease';

export interface LeaseTransitionActionsProps {
  state: string;
  /** Applies a transition. Cancelling carries the stated reason. */
  onApply: (action: LeaseAction, body?: LeaseTransitionBody) => Promise<void>;
  /** Rendered to the left of the transitions, e.g. "Nova vistoria". */
  children?: ReactNode;
}

/**
 * Pull the backend's own refusal message out of a failed transition.
 *
 * The service answers 422 naming exactly what is missing ("Cannot
 * activate: missing landlords, billing_locale, warranty_decision", or
 * "Cannot rescind: expectation(s) already received payment: 2026-11."),
 * which is the only part worth showing -- a generic "not allowed" would
 * leave the broker with nothing to act on.
 *
 * @param error - The thrown request error.
 * @returns The backend's message, or undefined when it did not send one.
 */
function refusalReason(error: unknown): string | undefined {
  const body = (error as { body?: { error?: string } } | undefined)?.body;
  return body?.error;
}

/**
 * Which actions are destructive enough to be painted as such.
 *
 * Rescission ends a live contract; cancellation throws a draft away and
 * hands the property back to the market. Neither can be walked back
 * through any route the API exposes.
 */
const DANGER_ACTIONS: ReadonlySet<string> = new Set(['rescind', 'cancel']);

/**
 * Which actions the backend refuses without a written reason.
 *
 * `cancel` is the only one: `LeaseService.cancel` answers `422 Cannot
 * cancel: a reason is required.` for a blank one, so clicking the button
 * opens the panel below instead of posting a transition that is certain
 * to be refused.
 */
const REASON_ACTIONS: ReadonlySet<string> = new Set(['cancel']);

/**
 * Which actions ask "are you sure?" before posting.
 *
 * Rescission takes no reason, but it ends a live contract and cancels
 * every future unpaid month in one go, with no route back. It used to fire
 * on the first click, so a misclick on the header ended the contract.
 */
const CONFIRM_ACTIONS: ReadonlySet<string> = new Set(['rescind']);

/** The action that should read as the primary thing to do, per state. */
const PRIMARY_ACTIONS: ReadonlySet<string> = new Set(['activate', 'resume']);

/**
 * The action row: only the lifecycle transitions valid from the lease's
 * current state [Plano 3, Fase 7, SC-3], styled per the approved prototype.
 *
 * The graph is mirrored from the backend and the backend still validates
 * the edge, so a refusal is expected rather than exceptional -- which is
 * why the message is kept on screen instead of only flashed in a toast.
 *
 * `cancel` is offered on a draft as of the pós-aluguel plan's Etapa 4,
 * which taught the transitions route to dispatch the `draft -> cancelled`
 * edge the state machine had always declared. It is the one transition
 * that cannot be fired straight from its button: the backend requires a
 * written reason, so the click opens {@link CancelReasonPanel} and the
 * transition is posted from there.
 *
 * @param props.state - The lease's current state.
 * @param props.onApply - Applies the chosen transition.
 * @param props.children - Extra actions rendered before the transitions.
 * @returns The action row, or a terminal-state note.
 */
export function LeaseTransitionActions({
  state,
  onApply,
  children,
}: LeaseTransitionActionsProps) {
  const { t } = useTranslation('features/leases');
  const actionLabel = useLeaseActionLabel();
  const [pending, setPending] = useState<LeaseAction | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [asking, setAsking] = useState<LeaseAction | null>(null);
  const actions = getActionsForState(state);

  const run = async (action: LeaseAction, body?: LeaseTransitionBody) => {
    setPending(action);
    setRefusal(null);
    try {
      await onApply(action, body);
      setAsking(null);
    } catch (e) {
      const raw = refusalReason(e);
      const reason = raw ? describeLeaseRefusal(raw, t) : t('actionFailed');
      setRefusal(reason);
      toast.error(reason);
    } finally {
      setPending(null);
    }
  };

  const handleClick = (action: LeaseAction) => {
    if (REASON_ACTIONS.has(action) || CONFIRM_ACTIONS.has(action)) {
      setRefusal(null);
      setAsking((open) => (open === action ? null : action));
      return;
    }
    void run(action);
  };

  const offersRescind = actions.includes('rescind');

  return (
    <div className="flex flex-col items-start lg:items-end gap-(--space-2)">
      <div
        className="flex items-center justify-end gap-(--space-2) flex-wrap"
        data-testid="lease-transition-actions"
      >
        {children}

        {actions.map((action) => (
          <Button
            key={action}
            type="button"
            size="sm"
            variant={
              PRIMARY_ACTIONS.has(action)
                ? 'solid'
                : DANGER_ACTIONS.has(action) ? 'none' : 'ghost-text'
            }
            disabled={pending !== null}
            loading={pending === action}
            onClick={() => handleClick(action)}
            data-testid={`lease-action-${action}`}
            className={DANGER_ACTIONS.has(action)
              ? 'bg-transparent border border-(--status-error-border) text-(--status-error-text) hover:bg-(--status-error-bg)'
              : undefined}
          >
            {actionLabel(action)}
          </Button>
        ))}

        {actions.length === 0 && (
          <Text component="span" className="text-(length:--text-meta) text-(--text-tertiary)">
            {t('terminalState')}
          </Text>
        )}
      </div>

      {asking === 'cancel' && (
        <CancelReasonPanel
          busy={pending === 'cancel'}
          disabled={pending !== null}
          onConfirm={(reason) => void run('cancel', { reason })}
          onDismiss={() => setAsking(null)}
        />
      )}

      {asking === 'rescind' && (
        <RescindConfirmPanel
          busy={pending === 'rescind'}
          disabled={pending !== null}
          onConfirm={() => void run('rescind')}
          onDismiss={() => setAsking(null)}
        />
      )}

      {offersRescind && (
        <Text
          component="span"
          data-testid="lease-rescind-note"
          className="text-(length:--text-micro) text-(--text-tertiary) lg:text-right"
        >
          {t('detail.rescindNote')}
        </Text>
      )}

      {refusal && (
        <Text
          component="span"
          role="alert"
          data-testid="lease-transition-refusal"
          className="text-(length:--text-meta) text-(--status-error-text) lg:text-right max-w-[52ch]"
        >
          {refusal}
        </Text>
      )}
    </div>
  );
}

/**
 * The reason a draft cancellation cannot be sent without.
 *
 * Modelled on the sale's "Desfazer venda" panel: an inline block rather
 * than a dialog, because the reader has already committed to the action
 * by clicking and only owes one sentence. The confirm stays disabled
 * while the box is blank or only whitespace — the same emptiness the
 * backend rejects — so the button never fires a request that is certain
 * to come back 422.
 *
 * @param props.busy - Whether the cancellation is in flight.
 * @param props.disabled - Whether any transition is in flight.
 * @param props.onConfirm - Sends the cancellation with its trimmed reason.
 * @param props.onDismiss - Closes the panel without cancelling.
 * @returns The rendered panel.
 */
function CancelReasonPanel({
  busy,
  disabled,
  onConfirm,
  onDismiss,
}: {
  busy: boolean;
  disabled: boolean;
  onConfirm: (reason: string) => void;
  onDismiss: () => void;
}) {
  const { t } = useTranslation('features/leases');
  const [reason, setReason] = useState('');

  return (
    <div
      data-testid="lease-cancel-panel"
      className="flex flex-col gap-(--space-2) w-full max-w-[52ch] text-left border-t border-(--border-subtle) pt-(--space-3)"
    >
      <Text
        component="span"
        className="text-(length:--text-body-sm) [font-weight:var(--font-semibold)] text-(--text-primary)"
      >
        {t('detail.cancel.title')}
      </Text>

      <Textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder={t('detail.cancel.reasonPlaceholder')}
        aria-label={t('detail.cancel.reasonLabel')}
        data-testid="lease-cancel-reason-input"
        className="min-h-16"
      />

      <Text
        component="span"
        className="text-(length:--text-micro) text-(--text-tertiary)"
      >
        {t('detail.cancel.note')}
      </Text>

      <div className="flex flex-wrap items-center gap-(--space-2)">
        <Button
          type="button"
          size="sm"
          variant="none"
          disabled={disabled || !reason.trim()}
          loading={busy}
          data-testid="lease-cancel-confirm"
          onClick={() => onConfirm(reason.trim())}
          className="bg-(--status-error-text) text-(--text-inverse) hover:opacity-90"
        >
          {t('detail.cancel.confirm')}
        </Button>

        <Button
          type="button"
          size="sm"
          variant="ghost-text"
          data-testid="lease-cancel-dismiss"
          onClick={onDismiss}
        >
          {t('detail.cancel.dismiss')}
        </Button>
      </div>
    </div>
  );
}

/**
 * The last word before a rescission is posted.
 *
 * Same inline shape as {@link CancelReasonPanel}, minus the reason the
 * backend does not take for this transition.
 *
 * @param props.busy - Whether the rescission is in flight.
 * @param props.disabled - Whether any transition is in flight.
 * @param props.onConfirm - Posts the rescission.
 * @param props.onDismiss - Closes the panel without rescinding.
 * @returns The rendered panel.
 */
function RescindConfirmPanel({
  busy,
  disabled,
  onConfirm,
  onDismiss,
}: {
  busy: boolean;
  disabled: boolean;
  onConfirm: () => void;
  onDismiss: () => void;
}) {
  const { t } = useTranslation('features/leases');

  return (
    <div
      data-testid="lease-rescind-panel"
      className="flex flex-col gap-(--space-2) w-full max-w-[52ch] text-left border-t border-(--border-subtle) pt-(--space-3)"
    >
      <Text
        component="span"
        className="text-(length:--text-body-sm) [font-weight:var(--font-semibold)] text-(--text-primary)"
      >
        {t('detail.rescind.title')}
      </Text>

      <Text component="span" className="text-(length:--text-micro) text-(--text-tertiary)">
        {t('detail.rescind.note')}
      </Text>

      <div className="flex flex-wrap items-center gap-(--space-2)">
        <Button
          type="button"
          size="sm"
          variant="none"
          disabled={disabled}
          loading={busy}
          data-testid="lease-rescind-confirm"
          onClick={onConfirm}
          className="bg-(--status-error-text) text-(--text-inverse) hover:opacity-90"
        >
          {t('detail.rescind.confirm')}
        </Button>

        <Button
          type="button"
          size="sm"
          variant="ghost-text"
          data-testid="lease-rescind-dismiss"
          onClick={onDismiss}
        >
          {t('detail.rescind.dismiss')}
        </Button>
      </div>
    </div>
  );
}

export default LeaseTransitionActions;
